import type { SedrFile } from '../../api/sedr';
import { fileTypeIconFor, fileTypeLabelKeyFor } from '../../files/fileTypes';
import { applyI18n, t } from '../../i18n';
import { enhanceDataTableCards } from '../../ui/dataTableCards';
import { bindFilesDropUpload } from '../../ui/filesDropUpload';
import { fileTypeImg } from '../../ui/fileTypeImg';
import { openFilesContextMenu } from '../../ui/filesContextMenu';
import { renderBreadcrumbNav } from '../../ui/filesBreadcrumb';
import {
  bindFilesBrowseMobileTree,
  FILES_MOBILE_LAYOUT_MQ,
  type FilesBrowseMobileTreeHandle,
} from '../../ui/filesBrowseMobile';
import { iconImg } from '../../ui/icons';
import { bindBrowseSplitResize } from '../../ui/splitPaneResize';
import {
  applyDirTreeNodeContent,
  collectSedrNameMatches,
  expandFoldersForSearchHits,
  fillHighlightedText,
  mountFilesTreeSearchBar,
  sedrAncestorKeys,
  stepSearchIndex,
  toSearchState,
  type FilesTreeSearchBarHandle,
  type FilesTreeSearchMatch,
} from '../../ui/filesTreeSearch';
import { showSedrMoveDialog } from './sedrMoveDialog';
import { showSedrFilePropertiesDialog } from './sedrPropertiesDialog';

export interface SedrDirectoryTreeNode {
  id: string | null;
  name: string;
  children: SedrDirectoryTreeNode[];
}

/** API omits null parentId (WhenWritingNull); treat missing and null as the same root parent. */
export function sameSedrParentId(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  return (a ?? null) === (b ?? null);
}

export function buildSedrDirectoryTree(files: SedrFile[]): SedrDirectoryTreeNode {
  const dirMap = new Map<string, SedrFile>();
  for (const f of files) {
    if (f.isDirectory) dirMap.set(f.id, f);
  }

  const root: SedrDirectoryTreeNode = { id: null, name: '/', children: [] };
  const nodeMap = new Map<string | null, SedrDirectoryTreeNode>();
  nodeMap.set(null, root);

  for (const dir of dirMap.values()) {
    getOrCreateNode(dir.id, dir.name, dir.parentId, dirMap, nodeMap, root);
  }

  const sortChildren = (node: SedrDirectoryTreeNode) => {
    node.children.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    for (const c of node.children) sortChildren(c);
  };
  sortChildren(root);
  return root;
}

function getOrCreateNode(
  id: string,
  name: string,
  parentId: string | null | undefined,
  dirMap: Map<string, SedrFile>,
  nodeMap: Map<string | null, SedrDirectoryTreeNode>,
  root: SedrDirectoryTreeNode,
): SedrDirectoryTreeNode {
  const existing = nodeMap.get(id);
  if (existing) return existing;

  const node: SedrDirectoryTreeNode = { id, name, children: [] };
  nodeMap.set(id, node);

  let parent: SedrDirectoryTreeNode;
  const normalizedParentId = parentId ?? null;
  if (normalizedParentId === null) {
    parent = root;
  } else {
    const parentFile = dirMap.get(normalizedParentId);
    if (parentFile) {
      parent = getOrCreateNode(parentFile.id, parentFile.name, parentFile.parentId, dirMap, nodeMap, root);
    } else {
      parent = root;
    }
  }
  parent.children.push(node);
  return node;
}

export function listSedrFolderContents(
  files: SedrFile[],
  parentId: string | null,
): { directories: SedrFile[]; fileItems: SedrFile[] } {
  const directories: SedrFile[] = [];
  const fileItems: SedrFile[] = [];
  for (const f of files) {
    if (!sameSedrParentId(f.parentId, parentId)) continue;
    if (f.isDirectory) directories.push(f);
    else fileItems.push(f);
  }
  const cmp = (a: SedrFile, b: SedrFile) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  directories.sort(cmp);
  fileItems.sort(cmp);
  return { directories, fileItems };
}

export function sedrFolderCrumbs(
  files: SedrFile[],
  currentId: string | null,
): { id: string | null; name: string }[] {
  if (currentId === null) return [];
  const byId = new Map<string, SedrFile>();
  for (const f of files) {
    if (f.isDirectory) byId.set(f.id, f);
  }
  const crumbs: { id: string | null; name: string }[] = [];
  let cur: string | null = currentId;
  while (cur !== null) {
    const dir = byId.get(cur);
    if (!dir) break;
    crumbs.unshift({ id: dir.id, name: dir.name });
    cur = dir.parentId ?? null;
  }
  return crumbs;
}

/** Parent directory path with trailing slash, e.g. `/` or `/Evidence/Reports/`. */
export function sedrFileParentPath(files: SedrFile[], file: SedrFile): string {
  const crumbs = sedrFolderCrumbs(files, file.parentId ?? null);
  if (crumbs.length === 0) return '/';
  return `/${crumbs.map((c) => c.name).join('/')}/`;
}

export function sedrSiblingNameConflict(
  files: SedrFile[],
  parentId: string | null,
  name: string,
  excludeId?: string,
): string | null {
  const lower = name.toLowerCase().trim();
  for (const f of files) {
    if (excludeId && f.id === excludeId) continue;
    if (sameSedrParentId(f.parentId, parentId) && f.name.toLowerCase() === lower) {
      return t('files.owner.nameInUse');
    }
  }
  return null;
}

export interface SedrEvidenceExplorerOptions {
  files: SedrFile[];
  readOnly: boolean;
  /** When false, hide upload/drop while still allowing folder create if not readOnly. Default true. */
  allowUpload?: boolean;
  formatSize: (bytes: number) => string;
  initialParentId?: string | null;
  initialSearch?: string;
  isBusy?: () => boolean;
  onFolderChange?: (parentId: string | null) => void;
  onSearchChange?: (query: string) => void;
  onOpenFile: (file: SedrFile) => void;
  onDownloadFile: (file: SedrFile) => void;
  onNewFolder: (parentId: string | null, name: string) => Promise<void>;
  onUploadFiles: (parentId: string | null, files: File[]) => void;
  onRename?: (file: SedrFile, newName: string) => Promise<void>;
  onMove?: (file: SedrFile, newParentId: string | null) => Promise<void>;
  /** When set, shows Apply folder set in the explorer chrome at root (admin). */
  onApplyFolderSet?: () => void;
}

export function renderSedrEvidenceExplorer(
  host: HTMLElement,
  options: SedrEvidenceExplorerOptions,
): { setCurrentFolder: (id: string | null) => void } {
  let currentParentId: string | null = options.initialParentId ?? null;
  let searchQuery = options.initialSearch ?? '';
  let searchMatches: FilesTreeSearchMatch[] = [];
  let searchIndex = -1;
  let searchHitEntryId: string | null = null;
  let searchBar: FilesTreeSearchBarHandle | null = null;
  const expandedPaths = new Set<string | null>([null]);
  let disposeDrop: (() => void) | undefined;
  let disposeBrowseSplit: (() => void) | undefined;
  let disposeMobileTree: (() => void) | undefined;
  let mobileTree: FilesBrowseMobileTreeHandle | undefined;

  const parentIdFromDropPath = (path: string): string | null => (path === '' ? null : path);

  const canMutate = () => !options.readOnly && !(options.isBusy?.() ?? false);
  const canUpload = () => !options.readOnly && options.allowUpload !== false && !(options.isBusy?.() ?? false);

  const folderKey = (id: string | null) => id ?? '';

  const applySearchQuery = (query: string, resetIndex: boolean) => {
    searchQuery = query;
    options.onSearchChange?.(searchQuery);
    searchMatches = collectSedrNameMatches(searchQuery, options.files);
    if (resetIndex) {
      searchIndex = -1;
      searchHitEntryId = null;
    } else if (searchHitEntryId) {
      searchIndex = searchMatches.findIndex((m) => m.entryKey === searchHitEntryId);
      if (searchIndex < 0) searchHitEntryId = null;
    }
    searchBar?.setResultCount(searchMatches.length);
    if (searchMatches.length > 0) {
      expandFoldersForSearchHits(
        searchMatches,
        (key) => expandedPaths.add(key === '' ? null : key),
        (key) => sedrAncestorKeys(options.files, key === '' ? null : key),
      );
    }
  };

  const stepSearch = (direction: -1 | 1) => {
    if (searchMatches.length === 0) {
      applySearchQuery(searchQuery, true);
      if (searchMatches.length === 0) return;
    }
    searchIndex = stepSearchIndex(searchIndex, direction, searchMatches.length);
    const match = searchMatches[searchIndex];
    if (!match) return;
    searchHitEntryId = match.entryKey;
    const parentId = match.parentKey === '' ? null : match.parentKey;
    if (currentParentId !== parentId) {
      currentParentId = parentId;
      options.onFolderChange?.(parentId);
    }
    ensureExpandedAncestors(parentId);
    refreshListing();
  };

  const render = () => {
    disposeDrop?.();
    disposeDrop = undefined;
    disposeBrowseSplit?.();
    disposeBrowseSplit = undefined;
    disposeMobileTree?.();
    disposeMobileTree = undefined;
    mobileTree = undefined;

    const canCreateFolder = !options.readOnly;
    const canApplyFolderSet = Boolean(options.onApplyFolderSet && !options.readOnly);
    const canUploadEvidence = !options.readOnly && options.allowUpload !== false;
    const compactActions = typeof window !== 'undefined' && window.matchMedia(FILES_MOBILE_LAYOUT_MQ).matches;
    const hasCreateActions = canCreateFolder || canApplyFolderSet || canUploadEvidence;

    let directoryActions = '';
    if (hasCreateActions && compactActions) {
      directoryActions = `
        <button type="button" class="icon-button files-explorer-add" id="sedr-evidence-add" data-i18n-aria-label="files.owner.addActions" data-i18n-title="files.owner.addActions" aria-haspopup="menu"></button>
        ${canApplyFolderSet ? `<button type="button" id="sedr-evidence-apply-folder-set" hidden${currentParentId !== null ? ' data-root-only="1"' : ''}></button>` : ''}
        ${canCreateFolder ? '<button type="button" id="sedr-evidence-new-folder" hidden></button>' : ''}
        ${canUploadEvidence ? '<input type="file" id="sedr-evidence-upload" multiple hidden />' : ''}
      `;
    } else {
      const newFolderButton = canCreateFolder
        ? `<button type="button" class="button ghost" id="sedr-evidence-new-folder"><span data-i18n="files.owner.newFolder"></span></button>`
        : '';
      const applyFolderSetButton = canApplyFolderSet
        ? `<button type="button" class="button ghost" id="sedr-evidence-apply-folder-set"${currentParentId !== null ? ' hidden' : ''}><span data-i18n="sedr.evidence.applyFolderSet"></span></button>`
        : '';
      const uploadButton = canUploadEvidence
        ? `<label class="button primary files-folder-panel__upload"><span data-i18n="files.owner.upload"></span><input type="file" id="sedr-evidence-upload" multiple hidden /></label>`
        : '';
      directoryActions = `${applyFolderSetButton}${newFolderButton}${uploadButton}`;
    }

    host.innerHTML = `
      <div class="files-browse-split">
        <div class="files-browse-tree-pane">
          <div id="sedr-evidence-tree-search"></div>
          <nav id="sedr-evidence-tree" class="files-dir-tree" role="tree"></nav>
        </div>
        <section class="files-browse-files">
          <div class="files-explorer-bar">
            <nav class="files-folder-breadcrumb" id="sedr-evidence-breadcrumb"></nav>
            <div class="files-explorer-bar__actions">
              ${directoryActions}
            </div>
          </div>
          <div class="files-table-wrap files-browse-files__table-wrap">
            <table class="data-table files-table">
              <thead>
                <tr>
                  <th data-i18n="sedr.evidence.colName"></th>
                  <th data-i18n="sedr.evidence.colType"></th>
                  <th data-i18n="sedr.evidence.colSize"></th>
                  <th class="data-table__actions-heading" data-i18n="files.owner.actions"></th>
                </tr>
              </thead>
              <tbody id="sedr-evidence-tbody"></tbody>
            </table>
          </div>
          <p class="muted" id="sedr-evidence-empty" hidden data-i18n="sedr.evidence.empty"></p>
          <p class="status-text" id="sedr-evidence-status" hidden></p>
          <progress class="files-upload-progress" id="sedr-evidence-progress" hidden></progress>
          <p class="error-text" id="sedr-evidence-error" hidden></p>
        </section>
      </div>
    `;

    const searchHost = host.querySelector('#sedr-evidence-tree-search') as HTMLElement;
    searchBar = mountFilesTreeSearchBar(searchHost, {
      placeholderKey: 'files.search.placeholder',
      initialQuery: searchQuery,
      onQueryChange: (query) => {
        applySearchQuery(query, true);
        refreshListing();
      },
      onStep: stepSearch,
    });
    applySearchQuery(searchQuery, false);

    bindChromeActions();
    const splitEl = host.querySelector('.files-browse-split') as HTMLElement | null;
    const explorerBar = host.querySelector('.files-explorer-bar') as HTMLElement | null;
    disposeBrowseSplit = splitEl ? bindBrowseSplitResize(splitEl) : undefined;
    if (splitEl && explorerBar) {
      mobileTree = bindFilesBrowseMobileTree({ root: splitEl, explorerBar });
      disposeMobileTree = () => mobileTree?.dispose();
    }
    refreshListing();
    applyI18n(host);
  };

  const bindChromeActions = () => {
    host.querySelector('#sedr-evidence-new-folder')?.addEventListener('click', handleNewFolder);
    host.querySelector('#sedr-evidence-apply-folder-set')?.addEventListener('click', () => {
      options.onApplyFolderSet?.();
    });

    const uploadInput = host.querySelector('#sedr-evidence-upload') as HTMLInputElement | null;
    if (uploadInput) {
      uploadInput.addEventListener('change', () => {
        const selected = Array.from(uploadInput.files ?? []);
        uploadInput.value = '';
        if (selected.length > 0) options.onUploadFiles(currentParentId, selected);
      });
    }

    const addButton = host.querySelector('#sedr-evidence-add') as HTMLButtonElement | null;
    if (addButton && !addButton.querySelector('.icon-wrap')) {
      addButton.append(iconImg('plus', 16));
    }
    addButton?.addEventListener('click', () => {
      const applyBtn = host.querySelector('#sedr-evidence-apply-folder-set') as HTMLButtonElement | null;
      const newFolderBtn = host.querySelector('#sedr-evidence-new-folder') as HTMLButtonElement | null;
      openFilesContextMenu({
        anchorRect: addButton.getBoundingClientRect(),
        items: [
          ...(applyBtn && currentParentId === null
            ? [{ id: 'apply-folder-set', labelKey: 'sedr.evidence.applyFolderSet', action: () => applyBtn.click() }]
            : []),
          ...(newFolderBtn
            ? [{ id: 'new-folder', labelKey: 'files.owner.newFolder', action: () => newFolderBtn.click() }]
            : []),
          ...(uploadInput
            ? [{ id: 'upload', labelKey: 'files.owner.upload', action: () => uploadInput.click() }]
            : []),
        ],
      });
    });

    disposeDrop?.();
    disposeDrop = undefined;
    const splitEl = host.querySelector('.files-browse-split') as HTMLElement | null;
    if (splitEl && !options.readOnly && options.allowUpload !== false) {
      disposeDrop = bindFilesDropUpload({
        root: splitEl,
        getCurrentPath: () => currentParentId ?? '',
        canUpload,
        isBusy: () => options.isBusy?.() ?? false,
        onDrop: (dropped, targetPath) => {
          options.onUploadFiles(parentIdFromDropPath(targetPath), dropped);
        },
        onRejectedEmpty: () => {
          const errorEl = host.querySelector('#sedr-evidence-error') as HTMLParagraphElement | null;
          if (errorEl) {
            errorEl.hidden = false;
            errorEl.textContent = t('files.drop.emptyRejected');
          }
        },
      });
    }
  };

  const refreshListing = () => {
    applySearchQuery(searchQuery, false);
    const tree = buildSedrDirectoryTree(options.files);
    const { directories, fileItems } = listSedrFolderContents(options.files, currentParentId);
    const crumbs = sedrFolderCrumbs(options.files, currentParentId);
    const searchState = toSearchState(searchQuery, searchMatches);

    const treeHost = host.querySelector('#sedr-evidence-tree') as HTMLElement | null;
    const breadcrumbHost = host.querySelector('#sedr-evidence-breadcrumb') as HTMLElement | null;
    const tbody = host.querySelector('#sedr-evidence-tbody') as HTMLTableSectionElement | null;
    const emptyEl = host.querySelector('#sedr-evidence-empty') as HTMLParagraphElement | null;
    if (!treeHost || !breadcrumbHost || !tbody || !emptyEl) return;

    const applyFolderSetBtn = host.querySelector('#sedr-evidence-apply-folder-set') as HTMLButtonElement | null;
    if (applyFolderSetBtn && !host.querySelector('#sedr-evidence-add')) {
      applyFolderSetBtn.hidden = currentParentId !== null;
    }

    renderTree(treeHost, tree, searchState);
    renderBreadcrumb(breadcrumbHost, crumbs);
    tbody.replaceChildren();
    renderTable(tbody, directories, fileItems);
    emptyEl.hidden = directories.length > 0 || fileItems.length > 0;
    applyI18n(host);
  };

  const navigateTo = (id: string | null) => {
    mobileTree?.closeTree();
    if (currentParentId === id) {
      return;
    }
    currentParentId = id;
    options.onFolderChange?.(id);
    ensureExpandedAncestors(id);
    refreshListing();
  };

  const ensureExpandedAncestors = (id: string | null) => {
    const byId = new Map<string, SedrFile>();
    for (const f of options.files) {
      if (f.isDirectory) byId.set(f.id, f);
    }
    let cur: string | null = id;
    // Expand parents only — leave the selected folder free to collapse.
    if (cur !== null) {
      const selected = byId.get(cur);
      cur = selected?.parentId ?? null;
    }
    while (cur !== null) {
      expandedPaths.add(cur);
      const dir = byId.get(cur);
      if (!dir) break;
      cur = dir.parentId ?? null;
    }
  };

  if (currentParentId) ensureExpandedAncestors(currentParentId);

  const renderTree = (
    treeHost: HTMLElement,
    tree: SedrDirectoryTreeNode,
    searchState: ReturnType<typeof toSearchState>,
  ) => {
    treeHost.replaceChildren();
    const list = document.createElement('ul');
    list.className = 'files-dir-tree__list';
    list.append(renderTreeNode(tree, 1, searchState));
    treeHost.append(list);
  };

  const renderTreeNode = (
    node: SedrDirectoryTreeNode,
    level: number,
    searchState: ReturnType<typeof toSearchState>,
  ): HTMLLIElement => {
    const li = document.createElement('li');
    li.className = 'files-dir-tree__item';

    const path = node.id ?? '';
    const hasChildren = node.children.length > 0;
    const expanded = expandedPaths.has(node.id);

    const row = document.createElement('div');
    row.className = 'files-dir-tree__row';
    row.dataset.treePath = path;

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'files-dir-tree__toggle';
    toggle.tabIndex = -1;
    toggle.textContent = hasChildren ? (expanded ? '▾' : '▸') : '';
    toggle.disabled = !hasChildren;
    toggle.setAttribute('aria-hidden', hasChildren ? 'false' : 'true');
    if (hasChildren) {
      toggle.addEventListener('click', (e) => {
        e.stopPropagation();
        if (expandedPaths.has(node.id)) expandedPaths.delete(node.id);
        else expandedPaths.add(node.id);
        refreshListing();
      });
    }

    const nameBtn = document.createElement('button');
    nameBtn.type = 'button';
    nameBtn.className = `files-dir-tree__node${currentParentId === node.id ? ' files-dir-tree__node--active' : ''}`;
    nameBtn.style.paddingLeft = `${Math.max(0, level - 1)}rem`;
    nameBtn.tabIndex = 0;
    nameBtn.dataset.treePath = path;
    nameBtn.dataset.hasChildren = String(hasChildren);
    nameBtn.dataset.expanded = String(expanded);
    nameBtn.setAttribute('role', 'treeitem');
    nameBtn.setAttribute('aria-level', String(level));
    nameBtn.setAttribute('aria-selected', String(currentParentId === node.id));
    if (hasChildren) nameBtn.setAttribute('aria-expanded', String(expanded));
    applyDirTreeNodeContent(nameBtn, {
      icon: fileTypeImg('folder', 16),
      name: node.name,
      folderKey: folderKey(node.id),
      search: searchState,
    });
    nameBtn.addEventListener('click', () => {
      if (currentParentId === node.id && hasChildren) {
        if (expanded) expandedPaths.delete(node.id);
        else expandedPaths.add(node.id);
        refreshListing();
        return;
      }
      navigateTo(node.id);
    });

    row.append(toggle, nameBtn);
    li.append(row);

    if (expanded && hasChildren) {
      const childList = document.createElement('ul');
      childList.className = 'files-dir-tree__list';
      childList.setAttribute('role', 'group');
      for (const child of node.children) {
        childList.append(renderTreeNode(child, level + 1, searchState));
      }
      li.append(childList);
    }

    return li;
  };

  const renderBreadcrumb = (bcHost: HTMLElement, crumbs: { id: string | null; name: string }[]) => {
    renderBreadcrumbNav(bcHost, {
      onRoot: () => navigateTo(null),
      rootCurrent: currentParentId === null,
      segments: crumbs.map((crumb) => ({
        label: crumb.name,
        current: crumb.id === currentParentId,
        onNavigate: () => navigateTo(crumb.id),
      })),
    });
  };

  const createNameButton = (icon: HTMLElement, label: string, onClick: () => void) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'files-table__name-button';
    const nameSpan = document.createElement('span');
    nameSpan.className = 'files-table__name-label';
    fillHighlightedText(nameSpan, label, searchQuery);
    button.append(icon, nameSpan);
    button.addEventListener('click', onClick);
    return button;
  };

  const createActionsCell = (
    visible: Array<{ labelKey: string; onClick: () => void }>,
    onMenu: (rect: DOMRect) => void,
  ) => {
    const actionsCell = document.createElement('td');
    actionsCell.className = 'data-table__actions';
    const wrap = document.createElement('div');
    wrap.className = 'data-table__actions-inner';
    for (const item of visible) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'button ghost files-table__action-button';
      button.textContent = t(item.labelKey);
      button.addEventListener('click', item.onClick);
      wrap.append(button);
    }
    const menuButton = document.createElement('button');
    menuButton.type = 'button';
    menuButton.className = 'icon-button files-table__menu-button';
    menuButton.setAttribute('aria-label', t('files.owner.actions'));
    menuButton.textContent = '⋯';
    menuButton.addEventListener('click', () => onMenu(menuButton.getBoundingClientRect()));
    wrap.append(menuButton);
    actionsCell.append(wrap);
    return actionsCell;
  };

  const renameEntry = async (entry: SedrFile) => {
    if (!options.onRename || !canMutate()) return;
    const { showInputDialog, invalidSharedFolderEntryName } = await import('../../ui/inputDialog');
    await showInputDialog({
      titleKey: entry.isDirectory ? 'files.owner.renameFolderTitle' : 'files.owner.renameFileTitle',
      labelKey: entry.isDirectory ? 'files.owner.renameFolderLabel' : 'files.owner.renameFileLabel',
      defaultValue: entry.name,
      maxLength: 200,
      validate: (name) => {
        const invalid = invalidSharedFolderEntryName(name);
        if (invalid) return invalid;
        return sedrSiblingNameConflict(options.files, entry.parentId ?? null, name, entry.id);
      },
      submit: async (name) => {
        await options.onRename!(entry, name);
      },
    });
  };

  const moveEntry = async (entry: SedrFile) => {
    if (!options.onMove || !canMutate()) return;
    const destination = await showSedrMoveDialog({
      tree: buildSedrDirectoryTree(options.files),
      currentParentId: entry.parentId ?? null,
      excludeId: entry.isDirectory ? entry.id : undefined,
    });
    if (destination === undefined) return;
    if (sameSedrParentId(destination, entry.parentId)) return;
    await options.onMove(entry, destination);
  };

  const showProperties = (entry: SedrFile) => {
    showSedrFilePropertiesDialog({
      file: entry,
      files: options.files,
      formatSize: options.formatSize,
    });
  };

  const openDirectoryMenu = (dir: SedrFile, anchorRect: DOMRect) => {
    openFilesContextMenu({
      anchorRect,
      items: [
        { id: 'open', labelKey: 'files.owner.open', action: () => navigateTo(dir.id) },
        {
          id: 'rename',
          labelKey: 'files.owner.rename',
          disabled: !canMutate() || !options.onRename,
          action: () => {
            void renameEntry(dir);
          },
        },
        {
          id: 'move',
          labelKey: 'files.owner.moveTo',
          disabled: !canMutate() || !options.onMove,
          action: () => {
            void moveEntry(dir);
          },
        },
        { id: 'properties', labelKey: 'files.owner.properties', action: () => showProperties(dir) },
      ],
    });
  };

  const openFileMenu = (file: SedrFile, anchorRect: DOMRect) => {
    openFilesContextMenu({
      anchorRect,
      items: [
        {
          id: 'preview',
          labelKey: 'files.owner.preview',
          hidden: !file.blobId,
          action: () => options.onOpenFile(file),
        },
        {
          id: 'download',
          labelKey: 'files.owner.download',
          hidden: !file.blobId,
          action: () => options.onDownloadFile(file),
        },
        {
          id: 'rename',
          labelKey: 'files.owner.rename',
          disabled: !canMutate() || !options.onRename,
          action: () => {
            void renameEntry(file);
          },
        },
        {
          id: 'move',
          labelKey: 'files.owner.moveTo',
          disabled: !canMutate() || !options.onMove,
          action: () => {
            void moveEntry(file);
          },
        },
        { id: 'properties', labelKey: 'files.owner.properties', action: () => showProperties(file) },
      ],
    });
  };

  const renderTable = (
    tbody: HTMLTableSectionElement,
    directories: SedrFile[],
    fileItems: SedrFile[],
  ) => {
    for (const dir of directories) {
      const tr = document.createElement('tr');
      if (searchHitEntryId === dir.id) {
        tr.classList.add('files-table__row--search-hit');
      }
      tr.dataset.entryId = dir.id;
      tr.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        openDirectoryMenu(dir, new DOMRect(e.clientX, e.clientY, 0, 0));
      });

      const nameCell = document.createElement('td');
      nameCell.append(createNameButton(fileTypeImg('folder', 16), dir.name, () => navigateTo(dir.id)));

      const typeCell = document.createElement('td');
      typeCell.className = 'files-table__type';
      typeCell.textContent = t('files.owner.folderType');

      const sizeCell = document.createElement('td');
      sizeCell.textContent = '—';

      tr.append(
        nameCell,
        typeCell,
        sizeCell,
        createActionsCell(
          [{ labelKey: 'files.owner.open', onClick: () => navigateTo(dir.id) }],
          (rect) => openDirectoryMenu(dir, rect),
        ),
      );
      tbody.append(tr);
    }

    for (const file of fileItems) {
      const tr = document.createElement('tr');
      if (searchHitEntryId === file.id) {
        tr.classList.add('files-table__row--search-hit');
      }
      tr.dataset.entryId = file.id;
      tr.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        openFileMenu(file, new DOMRect(e.clientX, e.clientY, 0, 0));
      });

      const nameCell = document.createElement('td');
      const icon = fileTypeImg(fileTypeIconFor(file.name, file.contentType), 16);
      if (file.blobId) {
        nameCell.append(createNameButton(icon, file.name, () => options.onOpenFile(file)));
      } else {
        const nameSpan = document.createElement('span');
        nameSpan.className = 'files-table__name-label';
        fillHighlightedText(nameSpan, file.name, searchQuery);
        nameCell.append(icon, nameSpan);
      }

      const typeCell = document.createElement('td');
      typeCell.className = 'files-table__type';
      typeCell.textContent = t(fileTypeLabelKeyFor(file.name, file.contentType));

      const sizeCell = document.createElement('td');
      sizeCell.textContent = file.sizeBytes != null ? options.formatSize(file.sizeBytes) : '—';

      const visibleActions = file.blobId
        ? [
            { labelKey: 'files.owner.preview', onClick: () => options.onOpenFile(file) },
            { labelKey: 'files.owner.download', onClick: () => options.onDownloadFile(file) },
          ]
        : [];

      tr.append(
        nameCell,
        typeCell,
        sizeCell,
        createActionsCell(visibleActions, (rect) => openFileMenu(file, rect)),
      );
      tbody.append(tr);
    }

    if (searchHitEntryId) {
      const hitRow = tbody.querySelector(`tr[data-entry-id="${CSS.escape(searchHitEntryId)}"]`);
      hitRow?.scrollIntoView?.({ block: 'nearest' });
    }

    const evidenceTable = host.querySelector('table.data-table') as HTMLTableElement | null;
    if (evidenceTable) {
      enhanceDataTableCards(evidenceTable);
    }
  };

  const handleNewFolder = async () => {
    const { showInputDialog } = await import('../../ui/inputDialog');
    const { invalidSharedFolderEntryName } = await import('../../ui/inputDialog');
    await showInputDialog({
      titleKey: 'files.owner.newFolderTitle',
      labelKey: 'files.owner.newFolderName',
      confirmLabelKey: 'files.owner.newFolderCreate',
      maxLength: 200,
      validate: (name) => {
        const invalid = invalidSharedFolderEntryName(name);
        if (invalid) return invalid;
        return sedrSiblingNameConflict(options.files, currentParentId, name);
      },
      submit: async (name) => {
        await options.onNewFolder(currentParentId, name);
      },
    });
  };

  render();

  return {
    setCurrentFolder: (id: string | null) => {
      currentParentId = id;
      options.onFolderChange?.(id);
      ensureExpandedAncestors(id);
      refreshListing();
    },
  };
}
